import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface CatalogItem {
  _id: string;
  name: string;
  type: 'product' | 'service';
  sku: string;
  hsnSac: string;
  description: string;
  unit: string;
  defaultRate: number;
  defaultDiscount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  isActive: boolean;
  createdAt: string;
}

export function useCatalog(showAll?: boolean) {
  return useQuery({
    queryKey: ['accounts', 'catalog', showAll],
    queryFn: async () => {
      const params = showAll ? '?showAll=1' : '';
      const { data } = await api.get<CatalogItem[]>(`/api/accounts/catalog${params}`);
      return data;
    },
  });
}

export function useCatalogSearch(query: string) {
  return useQuery({
    queryKey: ['accounts', 'catalog', 'search', query],
    queryFn: async () => {
      if (!query || query.length < 1) return [];
      const { data } = await api.get<CatalogItem[]>(`/api/accounts/catalog/search?q=${encodeURIComponent(query)}`);
      return data;
    },
    enabled: query.length >= 1,
  });
}

export function useCreateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CatalogItem>) => {
      const { data } = await api.post('/api/accounts/catalog', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', 'catalog'] }),
  });
}

export function useUpdateCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CatalogItem> & { id: string }) => {
      const { data } = await api.put(`/api/accounts/catalog/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', 'catalog'] }),
  });
}

export function useDeleteCatalogItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/api/accounts/catalog/${id}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', 'catalog'] }),
  });
}
